/**
 * How each pet's abilities look in battle (Pets v3, docs/PETS.md "The roster"), by pet id: the sim's events are the same
 * for every pet (PetBit its attack, PetBarked its pulse, PetHowled its signature, PetMended the axolotl's Mend), so the
 * look is picked here. Colours are VFX colours 0–1; presentation only, no rule lives here.
 *
 * - `attack`: `snap` (sparks on the raider: the pup's bite, the tortoise's bash, the magpie's peck), `swoop` (a streak
 *   from the pet to the raider: the owl), `pounce` (a burst of flame: the kit), `spit` (a bubble flung: the axolotl).
 * - `pulse`: the rings and the glow round the flag; `pulseFlames` the kit's ring of fire; `pulseRise` sparkles rising
 *   on the troops it helped (the tortoise's ward, the axolotl's tide pool).
 * - `signature`: its rings, and `fall` (moonlight or stars falling on the raiders), `quake` (dust and crystal debris),
 *   `bubble` (a bubble round the raider it seals).
 * - `status`: the glow over a raider it marked (the owl's lit raiders, the magpie's glint).
 */
export type PetAttackLook = "snap" | "swoop" | "pounce" | "spit";
export type Rgb = readonly [number, number, number];

export interface PetFxLook {
  readonly attack: PetAttackLook;
  readonly attackColour: Rgb;
  readonly pulseColour: Rgb;
  readonly pulseFlames: boolean;
  readonly pulseRise: boolean;
  readonly signatureColour: Rgb;
  readonly fall: boolean;
  readonly quake: boolean;
  readonly bubble: boolean;
  readonly status: Rgb;
}

const GOLD: Rgb = [1, 0.85, 0.35];

const PUP: PetFxLook = {
  attack: "snap", attackColour: [1, 0.95, 0.85], pulseColour: GOLD, pulseFlames: false, pulseRise: false,
  signatureColour: [0.75, 0.88, 1], fall: false, quake: false, bubble: false, status: GOLD,
};

export const PET_FX_LOOKS: Readonly<Record<string, PetFxLook>> = {
  muster_pup: PUP,
  lantern_owl: {
    attack: "swoop", attackColour: [1, 0.78, 0.4], pulseColour: [1, 0.72, 0.3], pulseFlames: false, pulseRise: false,
    signatureColour: [0.72, 0.82, 1], fall: true, quake: false, bubble: false, status: [1, 0.75, 0.35],
  },
  ember_kit: {
    attack: "pounce", attackColour: [1, 0.55, 0.18], pulseColour: [1, 0.45, 0.12], pulseFlames: true, pulseRise: false,
    signatureColour: [1, 0.4, 0.1], fall: false, quake: false, bubble: false, status: [1, 0.5, 0.15],
  },
  crystal_tortoise: {
    attack: "snap", attackColour: [0.55, 0.85, 1], pulseColour: [0.5, 0.8, 1], pulseFlames: false, pulseRise: true,
    signatureColour: [0.55, 0.8, 1], fall: false, quake: true, bubble: false, status: [0.5, 0.8, 1],
  },
  bubble_axolotl: {
    attack: "spit", attackColour: [0.6, 0.95, 1], pulseColour: [0.45, 0.95, 0.9], pulseFlames: false, pulseRise: true,
    signatureColour: [0.6, 0.95, 1], fall: false, quake: false, bubble: true, status: [0.6, 0.95, 1],
  },
  star_magpie: {
    attack: "snap", attackColour: [1, 0.97, 0.8], pulseColour: [1, 0.9, 0.45], pulseFlames: false, pulseRise: false,
    signatureColour: [1, 0.92, 0.55], fall: true, quake: false, bubble: false, status: [1, 0.9, 0.4],
  },
};

/** The look of pet `petId`'s abilities (an unknown pet: the pup's). */
export function petFxLook(petId: string | null): PetFxLook { return (petId ? PET_FX_LOOKS[petId] : undefined) ?? PUP; }

/** The heal glow (the axolotl's Mend and Tide Pool on a troop). */
export const HEAL_COLOUR: Rgb = [0.55, 1, 0.6];
/** A burning raider's embers (the kit's fire). */
export const BURN_COLOUR: Rgb = [1, 0.5, 0.15];
/** A dazzled raider's twinkle (the magpie's Starfall). */
export const DAZZLE_COLOUR: Rgb = [1, 1, 0.85];
/** A crystal shield on a troop (the tortoise's Crystal Ward). */
export const SHIELD_COLOUR = 0x7ec8ff;
